/* The app's ONE skeleton treatment — a stylesheet with no component, the same shape as
 * `photoBox.module.css` and `emberButton.module.css` beside it.
 *
 * ── What a skeleton is here, and what it is not ────────────────────────────────────────────────
 * It is the SHAPE of the content, drawn before the content exists: three rows of the activity
 * timeline are three rows of bars where the date, the name and the metrics will be. It is not a
 * grey rectangle standing in for a card, and it is not a spinner. The owner's words, 2 September
 * 2026: "szkielet w kształcie treści", and "ma nie być efektu skoków, ma być czyściutko".
 *
 * ── The surface owns the geometry, this file owns the treatment ────────────────────────────────
 * Exactly the split `photoBox` states and for the same reason: a declaration written on both
 * sides is settled by chunk order, which differs between the dev server and the build, and this
 * repo has already paid for that once. So a caller writes `<i className={`${skel.bar} ${own}`} />`
 * and its own stylesheet gives the width, the height and the place in the row. What is here is
 * the fill, the radius and the sheen — the three things that must look identical everywhere.
 *
 * ── The sheen ──────────────────────────────────────────────────────────────────────────────────
 * The owner asked for it by name and set its terms: neutral (never ember — a coloured sweep would
 * break the glow ban and read as cheap), slow, and barely a difference in brightness. One pass is
 * `--dur-skeleton` on a linear timing, and the two inks are `--skeleton-fill` and
 * `--skeleton-sheen`, which move in opposite directions per theme so the sweep is a BRIGHTENING on
 * a white card and on a night card both. See `tokens/colors.css`.
 *
 * ── `prefers-reduced-motion` ───────────────────────────────────────────────────────────────────
 * Handled by the app's single block in `tokens/base.css`, not by a second media query here. That
 * block collapses the duration AND holds a looping animation to one iteration, so the sweep runs
 * once, instantly, and the pseudo-element returns to the resting position its own rule declares —
 * which is off the bar's left edge, invisible. What is left is the static shape, which is exactly
 * what the preference asks for. The keyframes below are written so that resting state is the
 * still one; a future sheen must keep that property or the preference stops being honoured.
 *
 * ── Accessibility ──────────────────────────────────────────────────────────────────────────────
 * A bar is decorative: an `<i>` with no text has no accessible name and takes no focus, so
 * nothing here needs `aria-hidden`. What a screen reader needs is the WORD, and that is the
 * caller's: every card that draws a skeleton carries `aria-busy` and one visually-hidden sentence
 * — the pair, on the card, at every call site.
 *
 * Both follow the WAIT, never the read (21 September 2026). `useSkeletonGate`'s `waiting` outlives
 * the answer by the bars' minimum life, and a region still showing bars is still about to change;
 * reporting it settled at the moment the read landed is a claim a listener acts on and a sighted
 * trainer can see is false. Attribute, branch and sentence key on one value.
 *
 * That sentence's own rule is NOT here. It lived in this file until 2 September 2026 and was
 * imported as `skel.srOnly`, which named a hiding treatment after loading; it is
 * `shared/ui/visuallyHidden.module.css` now, where every other hidden sentence in the app can
 * reach it without importing the skeleton stylesheet to get at one class.
 */

/* The bar FADES IN, and that is the 150ms grace made visible (21 September 2026). Until then a bar
 * appeared at full opacity, often inside a section still playing its own 300ms entrance, so the
 * wait itself arrived as a pop in the middle of a settle. It is the same 120ms opacity the
 * hand-over uses, on the same curve: the bars fade in, and later they fade out into the content.
 *
 * No delay, deliberately: the fade IS the grace made visible, and a delay on top would stretch the
 * wait the gate already times. (The app's one `prefers-reduced-motion` block zeroes
 * `animation-delay` as well as the duration since 27 September 2026, so a still viewer is not the
 * reason any more; it was when this was written.) */
.bar {
  position: relative;
  overflow: hidden;
  background: var(--skeleton-fill);
  border-radius: var(--radius-bar);
  animation-name: skeleton-reveal;
  animation-duration: var(--dur-skeleton-reveal);
  animation-timing-function: var(--ease-screen-entrance);
  animation-fill-mode: both;
}

/* The same shape with NO sweep, for a box that is not waiting for anything: a failed read drawn in
 * the shape its answer would have taken, so the pane keeps its geometry and does not claim to still
 * be loading. Declared after `.bar::after` and at equal specificity, so it wins on order — both
 * classes go on the same element and both rules are in this one file.
 *
 * `.still` turns off BOTH treatments, and it has to: the fade above is the arrival of a wait, and a
 * failure did not arrive, it was always going to be this shape. The sweep is the pseudo-element's,
 * the fade is the bar's own, so they are switched off in two places. */
.still::after {
  content: none;
}

.still {
  animation-name: none;
}

/* A round bar, for an avatar disc or a ring's hole. The caller still supplies the box. */
.disc {
  border-radius: var(--r-circle);
}

/* The sweep. `translateX(-100%)` is the RESTING state on purpose — see the header. */
.bar::after {
  position: absolute;
  inset: 0;
  content: '';
  background-image: linear-gradient(
    90deg,
    transparent 0%,
    var(--skeleton-sheen) 50%,
    transparent 100%
  );
  transform: translateX(-100%);
  animation-name: sheen;
  animation-duration: var(--dur-skeleton);
  animation-timing-function: var(--ease-skeleton);
  animation-iteration-count: infinite;
}

/* ── The hand-over ────────────────────────────────────────────────────────────────────────────
 * The third and last piece of the loading policy, added 9 September 2026 on the owner's own
 * reference (motion.dev's React skeleton shimmer, whose stated shape is that the shimmer
 * TRANSITIONS to the revealed content). The first two pieces were already here: the box is
 * reserved by the surface before the answer lands, and the sheen above is drawn inside it. What
 * was missing was the moment between them — the bars were removed and the content appeared in the
 * same frame, which is a cut.
 *
 * `useSkeletonGate` hands this class out, and only after a skeleton has actually been on screen:
 * a read answered inside the 150ms grace draws no bars and gets no reveal, because there is
 * nothing to hand over FROM. The gate keeps it applied afterwards rather than taking it away
 * again, which is what makes this run exactly once — a CSS animation plays when an element starts
 * matching its rule, and stops being able to play the moment it is already matching it.
 *
 * It arrives in the SAME state update that retires the bars (21 September 2026), so the first
 * render that draws the content already carries the class. While the two were separate the content
 * painted at full opacity first and then blinked to transparent to fade in, which is the second of
 * the two jumps the owner reported. Nothing here changed to fix that; the gate did.
 *
 * ── Opacity, and nothing else, on purpose ────────────────────────────────────────────────────
 * The box under this is already the size it will be — that is the loading policy's first rule —
 * so a reveal that also moved would be inventing a jump the policy exists to forbid. Measured
 * across the hand-over with per-request latency injected: zero pixels of single-frame movement.
 *
 * `--dur-skeleton-reveal` is 120ms and it is meant to be missed. This is a hand-over, not an
 * entrance; the entrances are `shared/design-system/motion` and they are three times its length.
 * `prefers-reduced-motion` is the app's one block in `tokens/base.css`, which collapses the
 * duration and, with `both`, leaves the content at full opacity in its first frame. */
.reveal {
  animation-name: skeleton-reveal;
  animation-duration: var(--dur-skeleton-reveal);
  animation-timing-function: var(--ease-screen-entrance);
  animation-fill-mode: both;
}

@keyframes skeleton-reveal {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes sheen {
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(100%);
  }
}
